import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Per-chart error boundary.
 *
 * Wraps each chart so a runtime rendering error in one chart
 * does not crash the entire Performance page. Shows a friendly
 * fallback with the chart name and a Retry button that
 * re-mounts the child component.
 */
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[ChartErrorBoundary] ${this.props.chartLabel}:`, error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            border: '1px solid rgba(239,68,68,0.2)',
            bgcolor: 'rgba(239,68,68,0.02)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>
              {this.props.chartLabel || 'Chart'} — Something went wrong
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8rem' }}>
            This chart encountered a rendering error. Other charts are unaffected.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={this.handleReset}
            sx={{ fontWeight: 600, fontSize: '0.78rem' }}
          >
            Retry
          </Button>
        </Paper>
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary;
